import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"
import { useMnemonic } from "@/hooks/useMnemonic"
import { MnemonicLabel, extractLabelText } from "@/components/mnemonics/MnemonicLabel"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, mnemonic, children, ...props }, ref) => {
  const innerRef = React.useRef(null)
  React.useImperativeHandle(ref, () => innerRef.current)

  const active = useMnemonic(mnemonic, () => innerRef.current?.click(), {
    label: typeof children === "string" ? children : props["data-testid"] || "",
    disabled: !!props.disabled,
  })

  return (
    <TabsPrimitive.Trigger
      ref={innerRef}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      )}
      aria-keyshortcuts={active ? `Alt+${String(mnemonic).toUpperCase()}` : undefined}
      aria-label={active ? (props["aria-label"] || extractLabelText(children) || undefined) : props["aria-label"]}
      {...props}>
      {active ? <MnemonicLabel char={mnemonic}>{children}</MnemonicLabel> : children}
    </TabsPrimitive.Trigger>
  );
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
